// Available Favorites Card Component
// Displays favorite providers who are available on a specific date with quick booking links

import React from 'react';
import { Users, Clock, Calendar, Sparkles, MapPin } from 'lucide-react';

const AvailableFavoritesCard = ({ availableProviders, selectedDate, onScheduleNow }) => {
  if (!availableProviders || availableProviders.length === 0) {
    return null;
  }

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTimeSlot = (slot) => {
    return slot.displayLabel || slot.label || `${slot.startTime} - ${slot.endTime}`;
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-200 p-6 mb-4 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Sparkles className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-800">
            Quick Schedule
          </h3>
        </div>
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <Calendar className="w-4 h-4" />
          <span>{formatDate(selectedDate)}</span>
        </div>
      </div>

      {/* Subtitle */}
      <p className="text-sm text-gray-600 mb-4">
        {availableProviders.length === 1
          ? '1 favorite provider is available:'
          : `${availableProviders.length} favorite providers are available:`}
      </p>

      {/* Available Providers */}
      <div className="space-y-3">
        {availableProviders.map((provider) => {
          // Group slots by location
          const slotsByLocation = {};
          provider.availableSlots.forEach(slot => {
            const locationKey = slot.locationData?.name || 'No location specified';
            if (!slotsByLocation[locationKey]) {
              slotsByLocation[locationKey] = {
                locationData: slot.locationData,
                slots: []
              };
            }
            slotsByLocation[locationKey].slots.push(slot);
          });

          return (
            <div
              key={provider.providerId}
              className="bg-white rounded-lg border border-blue-200 p-4 transition-all hover:shadow-md"
            >
              {/* Provider Info */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3 flex-1">
                  {/* Avatar */}
                  {provider.profilePicture ? (
                    <img
                      src={provider.profilePicture}
                      alt={provider.providerName}
                      className="w-10 h-10 rounded-full object-cover border-2 border-blue-200"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center border-2 border-blue-200">
                      <Users className="w-5 h-5 text-blue-600" />
                    </div>
                  )}

                  {/* Name and Specialty */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-800">
                      {provider.providerName}
                    </p>
                    {provider.specialty && (
                      <p className="text-xs text-gray-500">
                        {provider.specialty}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Available Time Slots - Grouped by Location */}
              <div className="space-y-3">
                {Object.entries(slotsByLocation).map(([locationName, locationGroup], groupIndex) => (
                  <div key={groupIndex} className="space-y-2">
                    {/* Location header */}
                    <div className="flex items-center space-x-2 text-xs text-gray-600">
                      <MapPin className="w-3.5 h-3.5" />
                      <span className="font-medium">{locationName}</span>
                    </div>

                    {/* Time slot buttons for this location */}
                    <div className="flex flex-wrap gap-2 ml-5">
                      {locationGroup.slots.slice(0, 3).map((slot, index) => (
                        <button
                          key={slot.id || index}
                          onClick={() => onScheduleNow(provider, slot)}
                          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors flex items-center space-x-1"
                        >
                          <span>{formatTimeSlot(slot)}</span>
                        </button>
                      ))}

                      {locationGroup.slots.length > 3 && (
                        <button
                          onClick={() => onScheduleNow(provider, locationGroup.slots[0])}
                          className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-md transition-colors"
                        >
                          +{locationGroup.slots.length - 3} more
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer Note */}
      <div className="mt-4 pt-4 border-t border-blue-200">
        <p className="text-xs text-gray-500 text-center">
          Click a time to start scheduling a meeting with this provider
        </p>
      </div>
    </div>
  );
};

export default AvailableFavoritesCard;
